import { Button, DialogContent, DialogActions, TextField, CircularProgress, Autocomplete, Chip, Box, Typography, Link, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { getStore, useStore } from "../../../store/store";
import { RecordContextType } from "../../../components/record/Record";
import { useRelatedRecords } from "../../../api/useRelatedRecords";
import { ClientAndRelativesEmailSelectionField } from "../../../components/fieldsInputs/fields/customized/ClientAndRelativesEmailSelectionField";
import { api, baseUrl, servicesUrl } from "../../../api/config";
import { useUser } from "../../../api/useAuth";
import { useAvailableInstruments, AvailableInstrument } from "../../../api/useAvailableInstruments";
import { ROLES } from "../../../utils/constants";

type OpenInstrument = {
	response_id: string;
	instrument_id: string;
	instrument_name: string | null;
	status: "sent" | "in_progress";
	sent_at: string | null;
	expired: boolean;
};

type GeneratedInstrument = {
	instrument_id: string;
	response_id: string;
	instrument_name?: string;
};

type GenerateLinksResponse = {
	instruments: Array<GeneratedInstrument>;
	password: string;
};

const SHOW_GENERATED_LINKS = false;

type GenerateInstrumentLinksDialogProps = {
	context: any;
	onClose: () => void;
};

export const GenerateInstrumentLinksDialog = ({ context, onClose }: GenerateInstrumentLinksDialogProps) => {
	const { notifications } = useStore();
	const { data: user } = useUser();
	const isTechAdmin = user?.role?.id === ROLES.tech_admin;
	const [isGenerating, setIsGenerating] = useState(false);
	const [isLoadingOpen, setIsLoadingOpen] = useState(true);
	const [openInstruments, setOpenInstruments] = useState<Array<OpenInstrument>>([]);
	const [result, setResult] = useState<GenerateLinksResponse | null>(null);

	const isLeadContext = context?.entity === "leads";
	const lead = isLeadContext ? context?.item : null;
	const treatment = isLeadContext ? null : context?.item;
	const client = treatment?.client;
	const responsibleForTreatment = treatment?.relative_resposible_for_treatment?.[0]?.relatives_id;

	const clientData = isLeadContext
		? {
				name: `${lead?.first_name ?? ""} ${lead?.last_name ?? ""}`.trim(),
				email: lead?.email,
		  }
		: {
				name: `${client?.user_id?.first_name ?? ""} ${client?.user_id?.last_name ?? ""}`.trim(),
				email: client?.user_id?.email,
		  };

	const { data: relatives } = useRelatedRecords(
		"relatives",
		"*.*",
		isLeadContext ? "lead" : "client_id",
		isLeadContext ? lead?.id : client?.id
	);

	const relativesData = relatives?.items?.map((relative: { Nome: string; email: string }) => ({
		name: relative?.Nome || "",
		email: relative?.email || "",
	})) || [];

	const defaultEmailRecipient = responsibleForTreatment
		? { name: responsibleForTreatment.Nome || "", email: responsibleForTreatment.email || "" }
		: clientData;

	const {
		data: availableData,
		isFetching: isFetchingAvailable,
		error: availableError,
	} = useAvailableInstruments(
		isLeadContext ? { leadId: lead?.id, includeIntake: true } : { treatmentId: treatment?.id, includeIntake: true }
	);

	const availableInstruments = availableData?.instruments ?? [];
	const [userSelectedInstruments, setUserSelectedInstruments] = useState<Array<AvailableInstrument> | null>(null);
	const selectedInstruments = userSelectedInstruments ?? availableInstruments;

	const availableErrorMessage = availableError
		? (availableError as any)?.response?.data?.errors?.[0]?.message ||
		  (availableError as any)?.response?.data?.errorMessage ||
		  "Erro ao carregar instrumentos disponíveis"
		: null;

	const entityForm = useForm({
		defaultValues: {
			emailData: {
				id: `${defaultEmailRecipient.email}-${defaultEmailRecipient.name}`,
				name: defaultEmailRecipient.name,
				email: defaultEmailRecipient.email,
			},
		},
	});

	const contextRecordId = isLeadContext ? lead?.id : treatment?.id;

	useEffect(() => {
		const fetchOpenInstruments = async () => {
			if (!contextRecordId) {
				setIsLoadingOpen(false);
				return;
			}
			try {
				const { data } = await api({
					method: "GET",
					url: `${baseUrl}/instruments-forms/crm-responses`,
					params: isLeadContext ? { lead: contextRecordId, status: "open" } : { treatment: contextRecordId, status: "open" },
				});
				setOpenInstruments(data?.instruments ?? []);
			} catch (error: any) {
				notifications.setNotification({
					isOpen: true,
					message: error?.response?.data?.errors?.[0]?.message || error?.response?.data?.errorMessage || "Erro ao carregar questionários abertos",
					severity: "error",
				});
			} finally {
				setIsLoadingOpen(false);
			}
		};
		fetchOpenInstruments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contextRecordId]);

	const sendQuestionnaires = async (receiver: { name: string; email: string }, instrumentIds: Array<string>) => {
		notifications.setNotification({ isOpen: false });
		setIsGenerating(true);
		const requestData = {
			receiver,
			leadId: lead?.id ?? null,
			treatmentId: treatment?.id ?? null,
			instrumentIds,
			source: "direct-request",
		};
		try {
			const { data } = await api({ method: "POST", url: `${baseUrl}/instruments-forms/crm-responses/send`, data: requestData });
			notifications.setNotification({
				isOpen: true,
				message: `Instrumentos enviados com sucesso para ${receiver.email}`,
				severity: "success",
			});
			if (SHOW_GENERATED_LINKS && isTechAdmin) {
				setResult(data);
			} else {
				onClose();
			}
		} catch (error: any) {
			notifications.setNotification({
				isOpen: true,
				message: error?.response?.data?.errors?.[0]?.message || error?.response?.data?.errorMessage || "Erro ao gerar links",
				severity: "error",
			});
		} finally {
			setIsGenerating(false);
		}
	};

	const handleGenerate = () => {
		const emailData = entityForm.getValues("emailData");
		const receiver = { name: emailData.name, email: emailData.email };
		const instrumentIds = selectedInstruments.map((instrument) => instrument.id);
		sendQuestionnaires(receiver, instrumentIds);
	};

	return (
		<>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{isLoadingOpen && <CircularProgress color="secondary" size={24} />}
				{!isLoadingOpen && openInstruments.length > 0 && (
					<Box>
						<Typography variant="subtitle1" color="primary" fontWeight="bold" pb={1}>
							Instrumentos por preencher:
						</Typography>
						<Typography variant="body2" pb={1}>
							Os seguintes instrumentos já foram enviados ao cliente e ainda não foram preenchidos.
						</Typography>
						<Box sx={{ pl: 1.5, mb: 1.5 }}>
							{openInstruments.map((instrument) => (
								<Typography key={instrument.response_id} variant="body2">
									- {instrument.instrument_name ?? instrument.instrument_id}
								</Typography>
							))}
						</Box>
						<Divider sx={{ mt: 2, mb: 1 }} />
					</Box>
				)}
				{isFetchingAvailable ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2 }}>
						<CircularProgress color="secondary" size={20} />
						<Typography color="text.secondary">A verificar questionários disponíveis…</Typography>
					</Box>
				) : availableErrorMessage ? (
					<Typography color="error" sx={{ my: 2 }}>
						{availableErrorMessage}
					</Typography>
				) : (
					<Autocomplete
						multiple
						sx={{ minHeight: 56, my: 2 }}
						options={availableInstruments}
						value={selectedInstruments}
						onChange={(_event, newValue) => setUserSelectedInstruments(newValue)}
						getOptionLabel={(option) => option.name}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => <Chip label={option.name} {...getTagProps({ index })} key={option.id} />)
						}
						renderInput={(params) => <TextField {...params} label="Questionários" />}
					/>
				)}
				<ClientAndRelativesEmailSelectionField
					entityForm={entityForm as unknown as UseFormReturn}
					client={clientData}
					relatives={relativesData}
				/>
				{SHOW_GENERATED_LINKS && isTechAdmin && result && (
					<Box>
						<Divider sx={{ my: 1 }} />
						<Typography variant="subtitle1" color="primary" fontWeight="bold" pb={1}>
							Links gerados
						</Typography>
						<Box display="flex" flexDirection="column" gap={1}>
							{result.instruments.map((instrument) => (
								<Link
									key={instrument.response_id}
									href={`${servicesUrl}/instrumento/${instrument.response_id}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{instrument.instrument_name ?? `Questionário ${instrument.response_id}`}
								</Link>
							))}
						</Box>
						<Typography variant="body2" pt={2}>
							Palavra-passe: <strong>{result.password}</strong>
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Fechar
				</Button>
				<Button onClick={handleGenerate} disabled={isGenerating || isLoadingOpen || isFetchingAvailable || !!availableErrorMessage || selectedInstruments.length === 0}>
					{isGenerating ? <CircularProgress color="inherit" size={24} /> : "Enviar"}
				</Button>
			</DialogActions>
		</>
	);
};

export const openGenerateInstrumentLinksDialog = (context: RecordContextType) => {
	const { appDialog } = getStore();

	appDialog.createDialog({
		title: "Gerar Links de Instrumentos",
		contentAndActions: <GenerateInstrumentLinksDialog context={context} onClose={() => appDialog.toggleIsOpen()} />,
		allowSoftCancel: true,
	});

	return {
		showSuccessMessage: "no",
	};
};
